import React, { createContext, useState, useEffect, useContext } from 'react'
import {
  fetchPlayersOnTeam,
  PlayersOnTeam,
  saveAllStarVote,
  SubmitAllStarPlayer,
  EventGQL,
  PlayerAllStarType,
  SubmitAllStarObj,
  fetchSingleEventInfo,
} from '../../Api'
import { useAuthContext } from '../AuthContext/AuthContext'
import { CURRENT_YEAR, EVENTS_YEAR } from '../../Config/League/league.config'
import { hasNotStarted, isPastTime } from '../../Utils/logicHelper'
import { checkSatisfyTeamCheck, normalizeError, logEvent } from '../../Utils'
import { getUserId, getAuthStateDebug, validateAuthState } from '../../Utils'
import { PLL_CONFERENCES } from '../../Constant/season'
import { SnackbarContext } from '../SnackbarContext'
import { StorageContext } from '../StorageContext'
import { ANALYTICS_TAGS, WLL_ALL_STAR_SLUG } from '../../Constant'
import {
  ALL_STAR_CLOSE_DATE,
  ALL_STAR_SLUG,
  ALL_STAR_VOTING_START_DATE,
  ASG_VOTE_TEAM_LIMIT,
  STORAGE_NAMES,
} from '../../Constant'
import { fetchAllStarPlayers, fetchWLLAllStarPlayers } from 'Api/AllStar/allStar.api'
import { AllStarTeam } from 'Api/AllStar/allStar.types'
import { retrieveFromCache } from '../../Utils/windowHelpers/storageHelpers'

type AllStarContextType = {
  allStarEvent: EventGQL | null
  wllAllStarEvent: EventGQL | null
  allStarRosters: AllStarTeam[]
  wllAllStarRosters: AllStarTeam[]
  availablePlayers: PlayersOnTeam[]
  selectedPlayers: PlayersOnTeam[]
  selectedTeamOnePlayers: PlayersOnTeam[]
  selectedTeamTwoPlayers: PlayersOnTeam[]
  loading: boolean
  closed: boolean
  notStarted: boolean
  playerType: PlayerAllStarType
  showLoginPrompt: boolean
  selectPlayer: (player: PlayersOnTeam) => boolean
  removePlayer: (player: PlayersOnTeam) => boolean
  saveAllPicks: (email: string) => Promise<boolean>
  handlePlayerTypeChange: (pType: PlayerAllStarType) => void
  toggleLoginPrompt: () => void
}

export const AllStarContext = createContext<AllStarContextType | null>(null)

export const AllStarProvider: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const authContext = useAuthContext()
  const { authLoading } = authContext
  const { store, isStorageReady, storageStatuses } =
    useContext(StorageContext)!
  let { toggleShowSnackbar } = useContext(SnackbarContext)!
  const [allStarEvent, setAllStarEvent] = useState<EventGQL | null>(null)
  const [wllAllStarEvent, setWLLAllStarEvent] = useState<EventGQL | null>(null)
  const [allStarRosters, setAllStarRosters] = useState<AllStarTeam[]>([])
  const [wllAllStarRosters, setWLLAllStarRosters] = useState<AllStarTeam[]>([])
  const [availablePlayers, setAvailablePlayers] = useState<PlayersOnTeam[]>([])
  const [selectedPlayers, setSelectedPlayers] = useState<PlayersOnTeam[]>([]) //Full List of selected players
  //EAST
  const [selectedTeamOnePlayers, setSelectedTeamOnePlayers] = useState<PlayersOnTeam[]>(
    []
  )
  // WEST
  const [selectedTeamTwoPlayers, setSelectedTeamTwoPlayers] = useState<PlayersOnTeam[]>(
    []
  )
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [playerType, setPlayerType] = useState<PlayerAllStarType>('east')
  const [showLoginPrompt, setShowLoginPrompt] = useState<boolean>(false)

  const closed = isPastTime(ALL_STAR_CLOSE_DATE)
  const notStarted = hasNotStarted(ALL_STAR_VOTING_START_DATE)

  useEffect(() => {
    if (isStorageReady && !authLoading) {
      // Log auth state for debugging
      const authDebug = getAuthStateDebug(authContext, authLoading)
      const authValidation = validateAuthState(authContext)

      logEvent(ANALYTICS_TAGS.allstar_vote_status_check, {
        context: 'allstar_provider_init',
        authState: authDebug,
        authValid: authValidation.isValid,
        authIssues: authValidation.issues,
      })

      if (!authValidation.isValid) {
        console.warn('Auth state validation issues:', authValidation.issues)
      }

      fetchAllStarData()
    }
  }, [isStorageReady, authLoading, authContext])

  const fetchAllStarData = async () => {
    await Promise.all([
      fetchAllStarEvent('PLL'),
      fetchAvailablePlayers(),
      fetchPlayersOnTeams(),
    ])
    setLoading(false)
    await fetchAllStarEvent('WLL')
  }

  const fetchAllStarEvent = async (league: 'PLL' | 'WLL') => {
    try {
      const evt = await fetchSingleEventInfo(
        league === 'PLL' ? ALL_STAR_SLUG : WLL_ALL_STAR_SLUG
      )
      if (evt) {
        if (league === 'PLL') setAllStarEvent(evt)
        else setWLLAllStarEvent(evt)
      }
    } catch (err) {
      console.error('Failed to fetch All-Star event:', err)
    }
  }

  const fetchPlayersOnTeams = async () => {
    const event = await fetchAllStarPlayers()
    const wllEvent = await fetchWLLAllStarPlayers()
    if (event) {
      const teams = [event.awayTeam, event.homeTeam]
      setAllStarRosters(teams)
    }
    if (wllEvent) {
      const teams = [wllEvent.awayTeam, wllEvent.homeTeam]
      setWLLAllStarRosters(teams)
    }
  }

  const fetchAvailablePlayers = async () => {
    if (closed || notStarted) return Promise.resolve()
    //Check for if user previously selected
    const storedIds = await checkLocalStorage()

    //Fetch all selection options
    const players = await fetchPlayersOnTeam(EVENTS_YEAR)
    if (!players || players.allPlayers.length < 1) return

    //Find selected players from available using ids
    if (storedIds && storedIds.length > 0) {
      getSelectedPlayers(storedIds, players.allPlayers)
    }

    const sorted = players.allPlayers
      .filter((p) => p.stats?.gamesPlayed > 0)
      .sort(
        (a, b) =>
          b.stats.points - a.stats.points ||
          b.stats?.faceoffsWon - a.stats?.faceoffsWon ||
          b.stats?.saves - a.stats?.saves ||
          b.stats?.groundBalls - a.stats?.groundBalls
      )
    setAvailablePlayers(sorted)
  }

  const checkLocalStorage = async (): Promise<string[]> => {
    if (!isStorageReady) return []
    try {
      const result = await retrieveFromCache(
        STORAGE_NAMES.selectedPlayersASG,
        storageStatuses
      )
      if (result?.data && result.data.length > 0) {
        setSelectedIds(result.data)
        return result.data
      }
      return []
    } catch (err) {
      console.error('Error retrieving stored player selections:', err)
      logEvent(ANALYTICS_TAGS.allstar_vote_status_error, {
        context: 'player_selections_retrieval_error',
        error: err instanceof Error ? err.message : 'Unknown error',
      })
      return []
    }
  }

  const getSelectedPlayers = (playerIds: string[], availPls: PlayersOnTeam[]) => {
    let east: PlayersOnTeam[] = []
    let west: PlayersOnTeam[] = []
    if (playerIds.length > 0) {
      playerIds.map((id) => {
        const foundPlayer = availPls.find((pl) => pl.officialId === id)
        if (!foundPlayer) {
          return
        } else {
          const conf = foundPlayer?.currentTeam?.conference
          return conf === PLL_CONFERENCES.WESTERN
            ? west.push(foundPlayer)
            : east.push(foundPlayer)
        }
      })
    }
    setSelectedTeamOnePlayers(east)
    setSelectedTeamTwoPlayers(west)
    return setSelectedPlayers([...west, ...east])
  }

  const selectPlayer = (player: PlayersOnTeam) => {
    const validCheckResult = checkIfValidSelection(player)
    if (!validCheckResult.valid) {
      toggleShowSnackbar({
        text: validCheckResult.text,
        color: 'red',
      })
      return false
    }
    const conf = player?.currentTeam?.conference
    let sel = [...selectedPlayers, player]
    let selIds = [...selectedIds, player.officialId]
    if (conf === PLL_CONFERENCES.EASTERN) {
      setSelectedTeamOnePlayers((prev) => [...prev, player])
    } else {
      setSelectedTeamTwoPlayers((prev) => [...prev, player])
    }
    setSelectedPlayers(sel)
    setSelectedIds(selIds)

    if (isStorageReady) {
      store(STORAGE_NAMES.selectedPlayersASG, selIds).catch((err) => {
        console.error('Error storing player selection:', err)
        logEvent(ANALYTICS_TAGS.allstar_vote_status_error, {
          context: 'player_selection_storage_failed',
          playerId: player.officialId,
          totalSelected: selIds.length,
        })
      })
    }
    return true
  }

  const removePlayer = (player: PlayersOnTeam) => {
    console.log('Removing')
    const conf = player?.currentTeam?.conference
    let idList = [...selectedIds]
    let pList = [...selectedPlayers]
    pList = pList.filter((p) => p.officialId !== player.officialId)
    idList = idList.filter((id) => id !== player.officialId)
    if (conf === PLL_CONFERENCES.EASTERN) {
      setSelectedTeamOnePlayers((prev) =>
        prev.filter((p) => p.officialId !== player.officialId)
      )
    } else {
      setSelectedTeamTwoPlayers((prev) =>
        prev.filter((p) => p.officialId !== player.officialId)
      )
    }
    setSelectedPlayers(pList)
    setSelectedIds(idList)

    if (isStorageReady) {
      store(STORAGE_NAMES.selectedPlayersASG, idList).catch((err) => {
        console.error('Error storing updated player selection:', err)
        logEvent(ANALYTICS_TAGS.allstar_vote_status_error, {
          context: 'player_removal_storage_failed',
          playerId: player.officialId,
          totalSelected: idList.length,
        })
      })
    }
    return true
  }

  const saveAllPicks = async (email: string) => {
    console.log('Saving')
    if (closed || notStarted) {
      toggleShowSnackbar({
        text: 'Voting is closed',
        color: 'red',
      })
      return Promise.resolve(false)
    }
    if (selectedPlayers.length < 6) {
      toggleShowSnackbar({
        text: 'Please select more players',
        color: 'red',
      })
      return Promise.resolve(false)
    }
    const mappedTeamOne = mapPlayerSelections([...selectedTeamOnePlayers])
    const mappedTeamTwo = mapPlayerSelections([...selectedTeamTwoPlayers])
    const mappedSelections = [...mappedTeamOne, ...mappedTeamTwo]
    let input: SubmitAllStarObj = {
      email: email,
      year: CURRENT_YEAR,
      votes: mappedSelections,
    }

    const userId = getUserId(authContext)
    if (userId) {
      input.firebaseId = userId //Add user id if logged in

      logEvent(ANALYTICS_TAGS.allstar_vote_status_check, {
        context: 'vote_submission',
        userId: userId,
        hasUserId: true,
      })
    } else {
      logEvent(ANALYTICS_TAGS.allstar_vote_status_error, {
        context: 'vote_submission_no_user_id',
        authState: getAuthStateDebug(authContext, authLoading),
      })
    }
    try {
      const result = await saveAllStarVote(input)
      if (!result || result?.votes?.length < 1) return false
      const now = new Date().toDateString()

      if (isStorageReady) {
        try {
          await store(STORAGE_NAMES.allStarVoteDate, now)
        } catch (e: unknown) {
          const error = normalizeError(e)
          console.error('Error storing vote date:', error.message)
        }
      }
      return true
    } catch (e: unknown) {
      const error = normalizeError(e)
      console.error('Error submitting vote:', error.message)
      logEvent(ANALYTICS_TAGS.allstar_vote_status_error, {
        context: 'vote_submission_failed',
        userId: userId || 'anonymous',
        error: normalizeError(e),
      })
      toggleShowSnackbar({
        text: 'Error submitting vote',
        color: 'red',
      })
      return false
    }
  }

  const mapPlayerSelections = (list: PlayersOnTeam[]) => {
    let pList: SubmitAllStarPlayer[] = []
    list.map((p) => {
      const mappedPlayer = {
        playerId: p.officialId,
        teamId: p.currentTeam.officialId,
        position: p.currentTeam.position,
      }
      pList.push(mappedPlayer)
      return
    })
    return pList
  }

  const checkIfValidSelection = (player: PlayersOnTeam) => {
    const conf = player?.currentTeam?.conference
    const selectionsForTeam =
      conf === PLL_CONFERENCES.EASTERN ? selectedTeamOnePlayers : selectedTeamTwoPlayers
    if (conf === PLL_CONFERENCES.EASTERN) {
      if (selectedTeamOnePlayers.length === ASG_VOTE_TEAM_LIMIT) {
        //Limit max players
        return {
          valid: false,
          text: 'Eastern team is already full',
        }
      }
    }
    if (conf === PLL_CONFERENCES.WESTERN) {
      if (selectedTeamTwoPlayers.length === ASG_VOTE_TEAM_LIMIT) {
        return {
          valid: false,
          text: 'Western team is already full',
        }
      }
    }

    const result = checkSatisfyTeamCheck(player, selectionsForTeam, ASG_VOTE_TEAM_LIMIT)
    return result
  }

  const handlePlayerTypeChange = (pType: PlayerAllStarType) => {
    setPlayerType(pType)
  }

  const toggleLoginPrompt = () => {
    setShowLoginPrompt(!showLoginPrompt)
  }

  return (
    <AllStarContext.Provider
      value={{
        allStarEvent,
        wllAllStarEvent,
        allStarRosters,
        wllAllStarRosters,
        availablePlayers,
        selectedPlayers,
        selectedTeamOnePlayers,
        selectedTeamTwoPlayers,
        loading,
        closed,
        notStarted,
        playerType,
        showLoginPrompt,
        saveAllPicks,
        selectPlayer,
        removePlayer,
        handlePlayerTypeChange,
        toggleLoginPrompt,
      }}
    >
      {children}
    </AllStarContext.Provider>
  )
}
